import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import { acceptCoachInvitation } from '../../utils/invitationUtils';
import { toast } from 'react-hot-toast';
import Logo from '../layout/Logo';
import SocialLogin from './SocialLogin';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<any>(null);
  const { signIn } = useAuthStore();

  // Get invitation ID from URL parameters
  const invitationId = searchParams.get('invitation');

  // Load invitation details if invitation ID is provided
  useEffect(() => {
    if (invitationId) {
      loadInvitation();
    }
  }, [invitationId]);

  const loadInvitation = async () => {
    try {
      console.log('Loading invitation with ID:', invitationId);
      
      // First get the invitation
      const { data: invitation, error: invitationError } = await supabase
        .from('coach_invitations')
        .select('*')
        .eq('id', invitationId)
        .single();

      if (invitationError) {
        console.error('Error loading invitation:', invitationError);
        toast.error('Invalid or expired invitation link');
        return;
      }

      console.log('Found invitation:', invitation);

      if (invitation) {
        if (invitation.status !== 'pending') {
          console.error('Invitation is not pending:', invitation.status);
          toast.error('This invitation has already been used');
          return;
        }

        // Then get the team details
        const { data: team, error: teamError } = await supabase
          .from('teams')
          .select('id, name, sport')
          .eq('id', invitation.team_id)
          .single();

        if (teamError) {
          console.error('Error loading team:', teamError);
          toast.error('Failed to load team details');
          return;
        }

        // Combine the data
        const fullInvitation = {
          ...invitation,
          teams: team
        };

        setInvitation(fullInvitation);
        setEmail(invitation.email); // Pre-fill email from invitation
        toast.success(`Welcome back! Sign in to join ${team.name} as a ${invitation.role === 'head_coach' ? 'Head Coach' : 'Assistant Coach'}`);
      }
    } catch (error) {
      console.error('Error loading invitation:', error);
      toast.error('Failed to load invitation details');
    }
  };

  const processInvitation = async (user: any) => {
    if (!invitation || !user) return;

    try {
      const result = await acceptCoachInvitation(invitation, user);

      if (!result.success) {
        toast.error('Signed in and invitation accepted, but there was an issue with team membership. Please contact support.');
        return;
      }

      toast.success(`Successfully joined ${invitation.teams.name}!`);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('force-team-refresh'));
      }, 500);
      navigate(`/team/${invitation.team_id}`);
    } catch (error) {
      console.error('Error processing invitation:', error);
      toast.error('Signed in successfully but there was an issue processing your invitation.');
      navigate('/');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await signIn(email, password);
      
      // If there's an invitation, process it after successful login
      if (invitation) {
        // Wait a moment for the auth state to update
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await processInvitation(user);
        }
      } else {
        // Wait a moment for the session to establish before navigation
        await new Promise(resolve => setTimeout(resolve, 1000));
        navigate('/');
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center bg-[#3A3C3E] py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Logo className="h-16" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-white">
          {invitation ? `Join ${invitation.teams?.name}` : 'Sign in to your account'}
        </h2>
        {invitation ? (
          <p className="mt-2 text-center text-sm text-gray-300">
            Sign in to join as a {invitation.role === 'head_coach' ? 'Head Coach' : 'Assistant Coach'}
          </p>
        ) : (
          <p className="mt-2 text-center text-sm text-gray-300">
            Or{' '}
            <Link 
              to={invitationId ? `/register?invitation=${invitationId}` : '/register'} 
              className="font-medium text-primary hover:text-primary-hover underline"
            >
              create a new account
            </Link>
          </p>
        )}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-md rounded-xl sm:px-10">
          {invitation && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-800 rounded-xl p-3">
              <div className="font-medium">Coach Invitation</div>
              <div className="text-sm mt-1">
                Team: <strong>{invitation.teams?.name}</strong><br />
                Role: <strong>{invitation.role === 'head_coach' ? 'Head Coach' : 'Assistant Coach'}</strong><br />
                Sport: <strong>{invitation.teams?.sport}</strong>
              </div>
            </div>
          )}
          
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 rounded-xl p-3">
              {error}
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleLogin}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-dark">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!!invitation} // Disable email field if coming from invitation
                  className="w-full px-3 py-2 border border-stone rounded-xl focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              {invitation && (
                <p className="mt-1 text-xs text-gray-500">
                  Email is pre-filled from your invitation
                </p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="block text-sm font-medium text-dark">
                  Password
                </label>
                <Link to="/forgot-password" className="text-xs text-primary hover:text-primary-hover underline">
                  Forgot password?
                </Link>
              </div>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-stone rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-cream py-3 px-4 rounded-xl text-lg font-bold hover:bg-primary-hover transition disabled:opacity-50"
              >
                {loading 
                  ? (invitation ? 'Signing in & joining team...' : 'Signing in...') 
                  : (invitation ? 'Sign in & join team' : 'Sign in')
                }
              </button>
            </div>
          </form>

          <SocialLogin 
            mode="login" 
            invitation={invitation} 
            className="mt-6" 
          />
          
          {!invitation && (
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <Link to="/register" className="font-medium text-primary hover:text-primary-hover underline">
                  Create one here
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;